/* eslint-disable no-unneeded-ternary */
import { Injectable } from '@angular/core';
import { IRawApplication } from 'projects/common/src/app/interfaces/remote-models/raw-application';
import { CacheService } from 'projects/common/src/app/services';
import { TargetAppService } from '../../core/services/parameter';
import { MenuService } from '../../sidebar/services/menu.service';
import { Router } from '@angular/router';
import { AuthenticationService } from 'projects/authentication/src/app/services';
import { objectPathExists } from '../../../../../projects/common/src/app/services/utils/utilities';
import { IRawApplicationChild } from '../../../../../projects/common/src/app/interfaces/remote-models/raw-application-child';
import { ZoneHandlerService } from '../../dashboard/services/zone-handler/zone-handler.service';

@Injectable({
  providedIn: 'root',
})
export class InitNavigationService {
  constructor(
    private readonly cache: CacheService,
    private readonly targetApp: TargetAppService,
    private menuService: MenuService,
    private router: Router,
    private readonly auth: AuthenticationService,
    private zoneHandlerSvc: ZoneHandlerService,
  ) {}

  /**
   * Function to avoid the blank page at app component
   */
  public navigateToHome(): void {
    const path = window.location.href.replace('//', '');
    if (path.split('/')[1] === 'app' && !path.split('/')[2]) {
      setTimeout(() => {
        this.cache.saveSession('initializedApp', true);
        this.targetApp.getObservable().subscribe(
          (app: IRawApplication) => {
            if (app) {
              this.cache.saveSession('app', app);
              let securePath = app.childs[0];
              let urlPath = app.childs[0].url;

              let isDashboard;
              if (urlPath) {
                isDashboard = urlPath.toLowerCase() === 'dsh';
              }

              if (app.childs[0].childs.length > 0) {
                securePath = app.childs[0].childs[0];
                urlPath = `${app.childs[0].url}/${app.childs[0].childs[0].url}`;
              }
              const navigation = String(this.cache.session('prevNavigation'));
              let comparePath: string;

              if (navigation && navigation !== 'app' && navigation !== 'login' && navigation !== '') {
                comparePath = navigation;
              }

              comparePath = this.getPathToCompare(comparePath, securePath);

              const foundPath = this.findPath(app, comparePath);
              this.menuService.setSelection(foundPath ? foundPath : securePath);

              // TO DO: Fix routes for dsh.
              let navigateTo;
              if (foundPath) {
                navigateTo = navigation;
              } else if (isDashboard) {
                navigateTo = `/app/${urlPath}`;

                const pathZoneToVerify = navigation || navigateTo;
                this.zoneHandlerSvc.verifySelectedZone(pathZoneToVerify);
              } else {
                navigateTo = `/${securePath.url}`;
              }

              this.router.navigate([navigateTo]);
            } else {
              // Send to limbo and show an error message
              this.auth.cleanSessionCode('m006');
            }
          },
          () => {
            // Do something on error
          },
        );
      }, 1000); // Just visual
    }
  }

  /**
   * Function to get the path to compare and find the rigth URL
   *
   * @param comparePath paths to compare with nested paths
   * @param securePath default path if not found
   */
  private getPathToCompare(comparePath: string, securePath: IRawApplicationChild): string {
    if (comparePath) {
      const lastParameter = this.getLastParameterPath(comparePath);
      if (Number.isNaN(+lastParameter) || comparePath === '/') {
        comparePath = comparePath.charAt(0) === '/' ? comparePath.substr(1) : comparePath;
        comparePath = comparePath.match(/\//gi) ? comparePath.split('/')[comparePath.split('/').length - 1] : comparePath;
      }
    } else {
      comparePath = securePath.url;
    }
    return comparePath;
  }

  /**
   * Function to find redirect path
   *
   * @param app app to evaluate paths
   * @param comparePath paths to compare with nested paths
   */
  private findPath(app: IRawApplication, comparePath: string): IRawApplicationChild {
    const adjustedComparePath = comparePath.split('/').slice(2).join('/');

    const foundPath = app.childs.find((permission) => permission.path === adjustedComparePath);
    let findPath;

    if (!foundPath && objectPathExists(app, 'childs.length', true) && adjustedComparePath !== '') {
      const nestedPaths = this.getAllNestedRoutes(app.childs);

      // 1️⃣ Buscamos por `adjustedComparePath` exacto
      findPath = nestedPaths.find((route) => route.url === adjustedComparePath);

      // 2️⃣ Si no encontramos coincidencia exacta, buscamos por último segmento
      if (!findPath) {
        const lastParameter = this.getLastParameterPath(adjustedComparePath);
        findPath = nestedPaths.find((route) => route.url.endsWith(`/${lastParameter}`));
      }

      // 3️⃣ Si aún no encontramos, buscamos por `menuId`
      if (!findPath) {
        const expectedMenuId = this.getExpectedMenuId(adjustedComparePath, nestedPaths);
        findPath = nestedPaths.find((route) => route.menuId === expectedMenuId);
      }
    }

    return findPath || null;
  }

  private getExpectedMenuId(comparePath: string, nestedPaths: IRawApplicationChild[]): number {
    // 1️⃣ Intentamos coincidencia exacta en `nestedPaths`
    const exactMatch = nestedPaths.find((route) => comparePath.endsWith(route.url));
    if (exactMatch) {
      return exactMatch.menuId;
    }

    // 2️⃣ Buscamos por último segmento numérico en `comparePath`
    const lastParameter = this.getLastParameterPath(comparePath);
    if (!Number.isNaN(+lastParameter)) {
      const paramMatch = nestedPaths.find((route) => route.url.endsWith(`/${lastParameter}`));
      if (paramMatch) {
        return paramMatch.menuId;
      }
    }

    // 3️⃣ Si no encontramos nada, mostramos error
    return undefined;
  }
  /**
   * Flatten routes app array
   *
   * @param app allowed routes
   */
  private getAllNestedRoutes(app: IRawApplicationChild[]): IRawApplicationChild[] {
    const routes = [];
    for (const child of app) {
      routes.push({ ...child, url: child.url });
      if (Array.isArray(child.childs) && child.childs.length) {
        const nestedRoutes = this.getAllNestedRoutes(child.childs);
        nestedRoutes.forEach((nestedRoute) => {
          nestedRoute.url = `${child.url}/${nestedRoute.url}`;
          routes.push(nestedRoute);
        });
      }
    }
    return routes;
  }

  /**
   * Get last parameter of a navigation path.
   */
  private getLastParameterPath(path: string): string {
    if (!path || typeof path !== 'string') {
      return null;
    }
    const splittedPath = path.split('/');
    return splittedPath.length > 1 ? splittedPath[splittedPath.length - 1] : splittedPath[0];
  }
}
